import {
  SkeletonItem,
  SkeletonItemProps,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "@fluentui/react-components";
import { CSSProperties } from "react";
import { useSkeletonTableStyles } from "./skeletonTable.styles";
import { ISkeletonTableProps } from "./skeletonTable.types";

const DEFAULTS = {
  size: 20 as Required<SkeletonItemProps>["size"],
  rows: 15,
} as const;

function getColumnCount(columns?: string[], columnCount?: number): number {
  if (columns && columns.length > 0) return columns.length;
  return Math.max(1, columnCount ?? 3);
}

function pickWidth(i: number, provided?: Array<number | string>) {
  if (provided && provided[i] !== undefined) return provided[i];
  const widths = ["30%", "45%", "60%", "70%", "80%"];
  return widths[i % widths.length];
}

export const SkeletonTable = (props: ISkeletonTableProps): JSX.Element => {
  const {
    columns,
    columnCount,
    rows = DEFAULTS.rows,
    size = DEFAULTS.size,
    showHeaderSkeletons = true,
    columnWidths,
    height,
    fillContainer = false,
    className,
    style,
  } = props;

  const styles = useSkeletonTableStyles();
  const colCount = getColumnCount(columns, columnCount);
  const hasKnownColumns = Boolean(columns && columns.length > 0);

  const containerStyle: CSSProperties = {
    height: fillContainer ? "100%" : (height ?? undefined),
    maxHeight: height ? undefined : undefined,
    overflowY: height || fillContainer ? "hidden" : undefined,
    ...style,
  };

  return (
    <div className={className} style={containerStyle}>
      <Table
        role="table"
        aria-busy
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <TableHeader>
          <TableRow>
            {hasKnownColumns
              ? columns!.map((col) => (
                  <TableHeaderCell key={col} className={styles.headerCell}>
                    {col}
                  </TableHeaderCell>
                ))
              : Array.from({ length: colCount }).map((_, i) => (
                  <TableHeaderCell key={`h-${i}`} className={styles.headerCell}>
                    {showHeaderSkeletons ? (
                      <SkeletonItem
                        size={size}
                        style={{ width: pickWidth(i, columnWidths) }}
                      />
                    ) : (
                      <></>
                    )}
                  </TableHeaderCell>
                ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {Array.from({ length: rows }).map((_, r) => (
            <TableRow key={`r-${r}`}>
              {Array.from({ length: colCount }).map((__, c) => (
                <TableCell key={`c-${c}`}>
                  <SkeletonItem
                    size={size}
                    style={{ width: pickWidth(c, columnWidths) }}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
